import { useCallback, useEffect, useRef } from 'react';
import socket, { updateJoinParams } from '../socket';

const ACK_TIMEOUT_MS = 4000;
const RETRY_DELAYS_MS = [1000, 3000];

/**
 * Reports a student's scenario position to the live classroom socket.
 *
 * Fire-and-forget by design: navigation never waits on the socket. The REST
 * log (submitDecision) stays authoritative; this feed only drives the
 * teacher's live view. The server treats studentProgressUpdate as an upsert,
 * so a report is enough to rebuild state even after a server cold start.
 *
 * Delivery: ack with timeout, two retries with backoff. A newer report cancels
 * pending retries for an older one. As a final safety net the registered
 * joinAssignmentRoom replay params are kept current, so any reconnect restores
 * the latest position even if every retry was lost.
 */
const useAssignmentProgress = ({ assignmentId, userId, userInfo }) => {
    const lastReportedRef = useRef(null);
    const attemptTokenRef = useRef(0);
    const retryTimerRef = useRef(null);

    useEffect(() => () => {
        attemptTokenRef.current += 1;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    }, []);

    const reportProgress = useCallback((situationId) => {
        if (!assignmentId || !userId || !situationId) return;
        if (lastReportedRef.current === situationId) return;
        lastReportedRef.current = situationId;

        updateJoinParams('joinAssignmentRoom', { situationId });

        const token = ++attemptTokenRef.current;
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }

        const progressData = {
            studentId: userId,
            studentName: userInfo?.username,
            studentEmail: userInfo?.email,
            situationId,
            assignmentId,
        };

        const attempt = (attemptIndex) => {
            socket.timeout(ACK_TIMEOUT_MS).emit('studentProgressUpdate', progressData, (err, response) => {
                if (token !== attemptTokenRef.current) return; // superseded by a newer report
                if (!err && response?.status === 'OK') return;

                if (attemptIndex < RETRY_DELAYS_MS.length) {
                    retryTimerRef.current = setTimeout(() => {
                        if (token !== attemptTokenRef.current) return;
                        attempt(attemptIndex + 1);
                    }, RETRY_DELAYS_MS[attemptIndex]);
                } else {
                    console.warn('studentProgressUpdate unacknowledged; teacher view may lag until reconnect.', err || response);
                }
            });
        };

        attempt(0);
    }, [assignmentId, userId, userInfo?.username, userInfo?.email]);

    return { reportProgress };
};

export default useAssignmentProgress;
